import React from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import { TranslatedMessage } from "~i18n/translate";
import type { Person } from "~types/types";
import { getOpinionByline, getOpinionBybrowPrefix } from "~utils/opinionBybrow";

import styles from "./OpinionByline.module.css";

interface OpinionBylineProps {
    credits?: Person[];
}

const getBylineText = (credits: Person[]) => {
    if (!credits) {
        return null;
    }

    if (credits.length === 1) {
        const author = credits[0];
        const authorSuffix = getOpinionBybrowPrefix(credits);

        return `${author?.name}${authorSuffix ? ", " : ""}${authorSuffix}`;
    }

    return getOpinionByline(credits);
};

const isEditorialBoard = (credits: Person[]) => {
    if (!credits) {
        return false;
    }

    if (credits.length === 1) {
        const author = credits[0];
        return author.editorialBoard;
    }

    return getOpinionByline(credits) === "Editorial Board";
};

export const OpinionByline = ({ credits }: OpinionBylineProps) => {
    if (!credits) {
        return null;
    }

    const bylineText = getBylineText(credits);
    const showEditorialBoard = isEditorialBoard(credits);

    return (
        <div
            data-component="opinion-byline"
            className={clsx(
                styles.opinionByline,
                typographyStyles.articleLabel
            )}
        >
            {showEditorialBoard ? (
                <TranslatedMessage
                    id="editorialBoard"
                    defaultMessage="Editorial Board"
                />
            ) : (
                <TranslatedMessage
                    id="by"
                    defaultMessage="By {byline}"
                    values={{ byline: bylineText }}
                />
            )}
        </div>
    );
};
